import { type GitHubAppConnections, Permission } from "@octopusdeploy/octopus-server-client";
import { GitHubAppAuthCheck } from "~/areas/library/components/GitConnections/GitHubAppAuthCheck";
import { GitHubConnectionRepositorySelector, GitHubConnectionRepositorySummary, GitHubConnectionRepositoryHelpText } from "~/components/GitHub/GitHubConnectionRepositorySelector";
import { useGitHubConnectionRepositorySelector } from "~/components/GitHub/useGitHubConnectionRepositorySelector";
import { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import { ExpandableFormSection, Note, Summary } from "~/components/form";
import { GitConnectionOwner } from "./VersionControlSettingsConnectionDetails";
export interface GitHubVersionControlSettingsDetailsProps {
    spaceId: string;
    isNew: boolean;
    gitConnectionOwner: GitConnectionOwner;
    connections: GitHubAppConnections | undefined;
    refreshGitConnections: () => Promise<void>;
    url: string | undefined;
    connectionId: string | undefined;
    setGitHubConnectionDetails: (connectionId: string | undefined, url: string | undefined) => void;
}
export function GitHubVersionControlSettingsDetails({ spaceId, isNew, connections, refreshGitConnections, url, connectionId, setGitHubConnectionDetails, gitConnectionOwner }: GitHubVersionControlSettingsDetailsProps) {
    const gitHubRepositorySelectorState = useGitHubConnectionRepositorySelector(connections?.Connections, refreshGitConnections, connectionId, url, setGitHubConnectionDetails);
    const canViewLibraryGitCredentials = hasPermission(Permission.GitCredentialView);
    if (!canViewLibraryGitCredentials) {
        // If the user can't view library Git credentials, they can't load anything that we need
        // to fill out the dropdowns. In this case, show the URL if it's already connected, otherwise
        // show a message that they can't configure GitHub connections.
        const helpText = "You do not have permission to configure GitHub connections";
        let summary = Summary.summary(helpText);
        let contents = <Note>You do not have permission to change GitHub connections</Note>;
        if (url) {
            summary = Summary.summary(`${url}`);
            contents = (<Note>
                    Connected to repository <code>{url}</code>. You do not have permission to change GitHub connections
                </Note>);
        }
        return (<ExpandableFormSection errorKey="GitHubRepository" title="GitHub Repository" summary={summary} help={helpText} isExpandedByDefault={isNew}>
                {contents}
            </ExpandableFormSection>);
    }
    return (<>
            <GitHubAppAuthCheck actionDescription={gitConnectionOwner === GitConnectionOwner.Project ? "convert a project using a GitHub connection" : "connect a Git repository for the platform hub using a GitHub connection"}/>
            <ExpandableFormSection errorKey="GitHubRepository" title="GitHub Repository" summary={GitHubConnectionRepositorySummary(connectionId, url, gitConnectionOwner, gitHubRepositorySelectorState)} help={GitHubConnectionRepositoryHelpText(gitConnectionOwner)} isExpandedByDefault={isNew}>
                <GitHubConnectionRepositorySelector spaceId={spaceId} state={gitHubRepositorySelectorState}/>
            </ExpandableFormSection>
        </>);
}
